import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { signOut } from '@/lib/auth';

interface UserChipProps {
  user: User;
}

export default function UserChip({ user }: UserChipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const initials = getInitials(user.displayName, user.email);
  const label = user.displayName ?? user.email ?? 'Account';

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    navigate('/', { replace: true });
  }

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div className="user-chip-wrap" ref={ref}>
      <button
        type="button"
        className="user-chip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="user-avatar">{initials}</div>
        <div className="user-meta">
          <div>{label}</div>
          <small>NO SUB · PHASE 1</small>
        </div>
      </button>
      {open && (
        <div className="user-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => go('/welcome')}>
            Welcome / Tiers
          </button>
          <button type="button" role="menuitem" onClick={() => go('/account')}>
            Account
          </button>
          <button type="button" role="menuitem" onClick={() => go('/pricing')}>
            Pricing
          </button>
          <div className="user-menu-divider" />
          <button type="button" role="menuitem" className="danger" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function getInitials(displayName: string | null | undefined, email: string | null | undefined): string {
  if (displayName && displayName.trim()) {
    const parts = displayName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
    const combined = (first + last).toUpperCase();
    if (combined) return combined;
  }
  if (email && email.length > 0) {
    const ch = email[0];
    if (ch) return ch.toUpperCase();
  }
  return '·';
}
